/**
 * Past-time shading + the today line (MapPastTime.vue): a translucent bluelight
 * wash from the project start to today, plus the 3px primary-logo "today"
 * vertical. Projects only — the SceneRenderer skips it for templates.
 */
import { DateTime } from 'luxon';
import { START_Y } from '@/store/remapStore';
import { daysBetweenDates } from '@/utils/helpers/datetime';
import { cssVar } from '../../draw/palette';
import type { SceneContext } from '../../engine/SceneContext';

export function drawPastTime(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map } = scene;
  const project = scene.project.project(map.mapId);
  if (!project?.start_date) {
    return;
  }
  const start = DateTime.fromISO(project.start_date).toFormat('yyyy-LL-dd');
  const today = DateTime.now().toFormat('yyyy-LL-dd');
  const days = daysBetweenDates(start, today);
  if (days <= 0) {
    return;
  }

  const x0 = scene.screenX(0);
  const xToday = scene.screenX(days);
  const maskHeight = map.maskSize.height;

  // Past-time shading (var(--color-bluelight-200) @ 0.35).
  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = cssVar('--color-bluelight-200', '#B9E6FE');
  ctx.fillRect(x0, START_Y, xToday - x0, Math.max(0, maskHeight));
  ctx.restore();

  // Today line (var(--color-primary-logo), 3px). Wrapped so the 3px lineWidth +
  // strokeStyle don't leak into the next painter if drawTimeline early-returns.
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(xToday + 0.5, 140);
  ctx.lineTo(xToday + 0.5, scene.height);
  ctx.lineWidth = 3;
  ctx.strokeStyle = cssVar('--color-primary-logo', '#0808BC');
  ctx.stroke();
  ctx.restore();
}
